import * as React from 'react';
import type { Sys } from 'src/types/contentful-sdk-compat';
import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import SxProps from 'threads5/themes/sx-props';
import type { ITheme } from 'threads5/themes/theme';
import ThemeProvider from 'threads5/themes/theme-provider';
import { Wrapper } from 'src/containers';
import { Column } from 'src/components-v2/Layout';
import RichTextSwitch from 'src/containers/RichTextSwitch/RichTextSwitch';
import { useComponentTheme } from 'src/hooks/useComponentTheme';
import { useCmsMode } from 'src/contexts/ModeContext/ModeContext';
import { isProduction } from 'src/lib/constants';
import CmsLeftSectionalHeaderContent from './CmsLeftSectionalHeaderContent';

interface ICmsSectionalHeader {
  fields: any;
  sys: Sys;
  sx?: SxProps<ITheme>;
}

// TODO: curious why we aren't using sectional header here?
const CmsSectionalHeader = React.forwardRef(
  ({ fields, sys, sx = {} }: ICmsSectionalHeader, ref) => {
    const bgColor = fields?.backgroundColor?.toLowerCase() || 'white';
    const cmsMode = useCmsMode({
      mode: fields?.mode,
      backgroundColor: bgColor,
    });
    const componentTheme = useComponentTheme(cmsMode);
    const background = componentTheme.palette[bgColor] || bgColor;

    return (
      <ThemeProvider theme={componentTheme}>
        <Wrapper
          className='cms-sectional-header'
          ref={ref}
          component='header'
          sx={{
            background,
            // ...sx,
            ...fields?.styles,
          }}
        >
          <HeaderSwitch fields={fields} sys={sys} />
        </Wrapper>
      </ThemeProvider>
    );
  },
);

export default CmsSectionalHeader;

const HeaderSwitch = ({ fields, sys }) => {
  const inspectorProps = useContentfulInspectorMode({
    entryId: sys?.id,
  });
  switch (fields?.appearance) {
    case 'Centered':
      return (
        <Column
          xs={20}
          xsOffset={2}
          sm={18}
          smOffset={3}
          lg={16}
          lgOffset={4}
          sx={{
            textAlign: 'center',
            '& .eyebrow-heading': {
              mb: { xs: '1.8rem', md: '3.2rem' },
            },
            '& .decorative-text': {
              mb: { xs: '1.8rem', md: '3.2rem' },
            },
            '& h1:has(+ p:not(:empty))': {
              mb: { xs: '1.8rem', sm: '2.0rem', md: '3.2rem' },
            },
            '& h2:has(+ p:not(:empty))': {
              mb: { xs: '1.8rem', sm: '2.0rem', md: '3.2rem' },
            },
            '& p': {
              mb: '0.8rem',
            },
            '& p:nth-last-of-type(1):empty': {
              display: 'none',
            },
            '& .button-group': {
              justifyContent: 'center',
            },
          }}
          {...inspectorProps({ fieldId: 'leftCenteredBody' })}
        >
          <RichTextSwitch
            sys={sys}
            content={fields?.leftCenteredBody}
            nodes={{
              h1: {
                variant: 'h1',
                component: 'h2',
              },
              h2: {
                variant: 'h2',
                component: 'h3',
              },
              h6: {
                variant: 'h6',
                component: 'h2',
                className: 'eyebrow-heading',
              },
              p: {
                variant: 'p',
              },
            }}
          />
        </Column>
      );
    case 'Left':
      return (
        <Column xs={22} xsOffset={1} lg={20} lgOffset={2}>
          <CmsLeftSectionalHeaderContent fields={fields} sys={sys} />
        </Column>
      );
    case 'With pills':
      return (
        <Column
          xs={22}
          xsOffset={1}
          lg={20}
          lgOffset={2}
          {...inspectorProps({ fieldId: 'pillBody' })}
        >
          <RichTextSwitch
            sys={sys}
            content={fields?.pillBody}
            nodes={{
              h1: {
                variant: 'h1',
              },
              h2: {
                variant: 'h2',
                sx: {
                  lineHeight: { xs: '5.2rem', sm: '4.8rem', md: '9rem' },
                },
              },
            }}
          />
        </Column>
      );
    case 'Outlined':
      return (
        <Column
          xs={24}
          xsOffset={0}
          lg={20}
          lgOffset={2}
          {...inspectorProps({ fieldId: 'outlined' })}
        >
          <RichTextSwitch
            sys={sys}
            content={fields?.leftCenteredBody || fields?.pillBody}
            nodes={{
              h1: {
                component: 'h1',
                variant: 'h1',
              },
              h2: {
                component: 'h2',
                variant: 'h2',
                sx: {},
              },
              h3: {
                component: 'h3',
                variant: 'h3',
                sx: {},
              },
            }}
          />
        </Column>
      );
    default:
      if (!isProduction) {
        // eslint-disable-next-line no-console
        console.log('hmm something went wrong');
      }
      return null;
  }
};
